import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  BehaviorSubject,
  Subscription,
  concatMap,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  forkJoin,
  from,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { HrAlertService, HrHttpService, MrfAvailableBudgetView, MrfDocumentView, MRF_DOCUMENT_TYPE, SaveMrfFormPayload } from '../hr-http.service';
import {
  MRF_AADHAAR_DIGIT_LENGTH,
  MRF_PAN_LENGTH,
  formatMrfAmountForDisplay,
  formatMrfAmountWithCommas,
  formatMrfDateForDisplay,
  formatMrfDateForInput,
  formatMrfDateForPayload,
  getMrfAadhaarValidationError,
  getMrfExpectedSalaryValidationError,
  getMrfMobileValidationError,
  getMrfPanValidationError,
  isMrfReasonExit,
  normalizeMrfAadhaarDigits,
  normalizeMrfExpectedSalaryDigits,
  normalizeMrfGenderValue,
  normalizeMrfMobileDigits,
  normalizeMrfPanNo,
  normalizeMrfWorkflowStatus,
  parseMrfAmountNumber,
  formatMrfRequestStatusLabel,
  formatMrfOnboardingStatusLabel,
  getMrfRequestStatusTone,
  getMrfOnboardingStatusTone,
  resolveMrfOnboardingStatusRaw,
  resolveMrfRequestStatusRaw,
  syncMrfAadhaarControlFromInput,
  syncMrfExpectedSalaryControlFromInput,
  syncMrfMobileControlFromInput,
  syncMrfPanControlFromInput,
  resolveMrfQualifiedEmployeeName,
  resolveMrfRehiringAadharNo,
  resolveMrfRehiringAadharPreferringSaved,
  isMrfRehiringFromData,
  resolveMrfSurname,
  resolveMrfTempPayrollId,
  resolveMrfYearsOfExperience,
  EMPTY_MRF_HRMS_PERMISSIONS,
  MrfHrmsPermissions,
  setMrfUploadSizeError,
  clearMrfUploadSizeError,
  getMrfUploadFileSizeValidationMessage,
  getMrfUploadSizeError,
  MrfUploadErrorMap,
} from '../mrf-view-options';

type MrfPdDocSlot = 'aadhaar' | 'pan' | 'exam' | 'demo' | 'rehiringAadhaar';
type TempEmpControlValue = string | Record<string, unknown>;
type LeftEmpControlValue = string | Record<string, unknown>;
type ExamTakenMode = 'yes' | 'no';

interface QualifiedEmployeeExamSnapshot {
  employeeTempId: TempEmpControlValue;
  employeeName: string;
  surname: string;
  mobileNo: string;
  aadhaarCardNo: string;
  panNo: string;
  dob: string;
  gender: string;
  expectedSalary: string;
  lastConfirmedDob: string;
  empTypeUpdatedToConsultant: boolean;
  tempEmpOptionsList: Record<string, unknown>[];
  panPendingFiles: File[];
  aadhaarPendingFiles: File[];
  examPendingFiles: File[];
  demoVideoPendingFiles: File[];
  mrfDataFields: Record<string, unknown>;
}

const QUALIFIED_EMPLOYEE_MRF_KEYS = [
  'employeeName',
  'newEmpName',
  'newEmployeeName',
  'candidateName',
  'empName',
  'surname',
  'employeeSurname',
  'lastName',
  'mobileNo',
  'mobileNumber',
  'candidateMobile',
  'mobile',
  'phoneNo',
  'contactNo',
  'aadharNo',
  'aadhaarNo',
  'newEmpAadhar',
  'aadharCardNo',
  'candidateAadhar',
  'panNo',
  'panCardNo',
  'candidatePan',
  'panNumber',
  'panCardNumber',
  'pancardNo',
  'dob',
  'dateOfBirth',
  'employeeDob',
  'newEmpDob',
  'birthDate',
  'gender',
  'employeeGender',
  'sex',
  'expectedSalary',
  'expectedCTC',
  'salaryBudget',
  'salary',
  'expectedSal',
  'employeeTempId',
  'empTempId',
  'tempEmpId',
  'tempPayrollId',
  'prevChaitanyaId',
  'previousChaitanyaId',
  'previousEmployeeId',
  'prevEmpId',
] as const;

@Component({
  selector: 'app-mrf-principal-dean',
  templateUrl: './mrf-principal-dean.component.html',
  styleUrls: ['./mrf-principal-dean.component.scss'],
})
export class MrfPrincipalDeanComponent implements OnInit, OnDestroy {
  private static readonly TEMP_EMP_MIN_SEARCH_LENGTH = 5;
  private static readonly LEFT_EMP_MIN_SEARCH_LENGTH = 5;
  private static readonly MAX_EMPLOYEE_AGE = 58;
  private static readonly CONSULTANT_EMPLOYEE_TYPE = 'Consultant';
  private static readonly ATTEMPT_EXAM_MESSAGE =
    'Please attempt exam before moving forward.';

  mrfData: any = {};
  /** Qualified employee section — sourced only from getEmployeeAssessment. */
  employeeAssessmentData: Record<string, unknown> = {};
  private employeeAssessmentRaw: unknown = null;
  private employeeAssessmentErrored = false;
  entityName = '';
  budgetedPositions = '';
  noOfHeadcount = '1';
  availableHeadcount = '00';
  budgetAmount: number | null = null;
  totalUsedBudget: number | null = null;
  totalExpectedSalBudget: number | null = null;
  availableBudgetAmount: number | null = null;

  documents: any[] = [];
  aadhaarDocuments: any[] = [];
  panDocuments: any[] = [];
  examDocuments: any[] = [];
  demoVideoDocuments: any[] = [];
  rehireAadhaarDocuments: any[] = [];
  rehiring = false;
  /** When true, rehiring fields are read-only and bound only via LeftEmpSearch. */
  rehiringDataLocked = false;
  rehiringAadhaarPendingFiles: File[] = [];
  rehiringAadhaarUploadError = '';
  readonly maxRehiringAadhaarFiles = 1;
  prevChaitanyaId = new FormControl<LeftEmpControlValue>('');
  rehiringDob = new FormControl('');
  rehireAadharNo = new FormControl('');
  prevRehiringEmployeeDetails: Record<string, unknown> | null = null;
  prevLeftEmpOptionsList: Record<string, unknown>[] = [];
  private readonly prevLeftEmpOptionsSubject = new BehaviorSubject<Record<string, unknown>[]>([]);
  readonly prevLeftEmpOptions = this.prevLeftEmpOptionsSubject.asObservable();
  isSaving = false;
  aadhaarAlreadyExists = false;
  aadhaarVerifyInProgress = false;
  private lastAadhaarAlerted = '';
  private lastAadhaarBlockMessage = '';
  private lastAadhaarTempPayrollId = '';
  private blockApproveForMissingExam = false;
  panValidationError = '';
  private lastPanAlerted = '';
  uploadFileErrors: MrfUploadErrorMap = {};

  /** Entered by Principal/Dean at review (Section 6). */
  mobileNo = new FormControl('');
  panNo = new FormControl('');
  dob = new FormControl('');
  employeeName = new FormControl('');
  surname = new FormControl('');
  aadhaarCardNo = new FormControl('');
  gender = new FormControl('');
  employeeTempId = new FormControl<TempEmpControlValue>('');
  expectedSalary = new FormControl('');
  examTaken: 'yes' | 'no' = 'no';
  examTakenLockedByAadhaar = false;

  tempEmpOptionsList: Record<string, unknown>[] = [];
  private readonly tempEmpOptionsSubject = new BehaviorSubject<Record<string, unknown>[]>([]);
  readonly tempEmpOptions = this.tempEmpOptionsSubject.asObservable();
  private readonly subs = new Subscription();
  private lastConfirmedDob = '';
  private ageDialogOpen = false;
  private originalEmpType = '';
  private originalReplacementEmpId = 0;
  private empTypeUpdatedToConsultant = false;
  private examTakenYesSnapshot: QualifiedEmployeeExamSnapshot | null = null;
  private examTakenNoSnapshot: QualifiedEmployeeExamSnapshot | null = null;

  panPendingFiles: File[] = [];
  aadhaarPendingFiles: File[] = [];
  examPendingFiles: File[] = [];
  demoVideoPendingFiles: File[] = [];
  readonly maxAadhaarFiles = 1;
  readonly maxPanFiles = 1;
  readonly maxExamFiles = 1;
  readonly maxDemoFiles = 1;
  mrfPermissions: MrfHrmsPermissions = { ...EMPTY_MRF_HRMS_PERMISSIONS };
  private reportingManagersList: Record<string, unknown>[] = [];

  constructor(
    private spinner: NgxSpinnerService,
    private hrHttpService: HrHttpService,
    private route: ActivatedRoute,
    private router: Router,
    private hrAlert: HrAlertService
  ) {}

  ngOnInit(): void {
    this.setupTempEmpSearch();
    this.setupRehiringLeftEmpSearch();
    this.setupDobAgeValidation();
    this.setupAadhaarVerification();
    this.setupPanValidation();
    this.loadMrfFormData();
  }

  get disablePrincipalApprovalActions(): boolean {
    return (
      this.isSaving ||
      this.aadhaarAlreadyExists ||
      this.aadhaarVerifyInProgress ||
      !!this.panValidationError ||
      this.blockApproveForMissingExam
    );
  }

  displayTempEmpLabel = (item: TempEmpControlValue | null): string => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return this.getTempEmpOptionLabel(item);
  };

  onTempEmpSelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value as Record<string, unknown>;
    if (!item || typeof item !== 'object') return;
    this.applyTempEmpDetails(item);
  }

  refreshTempEmpAutocomplete(): void {
    const search = this.getTempEmpSearchText(this.employeeTempId.value);
    if (search.length < MrfPrincipalDeanComponent.TEMP_EMP_MIN_SEARCH_LENGTH) {
      this.publishTempEmpOptions(
        search ? this.filterTempEmpOptions(search, this.tempEmpOptionsList) : []
      );
      return;
    }
    this.subs.add(this.fetchTempEmpOptions(search).subscribe());
  }

  trackByTempEmpId = (index: number, item: Record<string, unknown>): string | number => {
    return this.hrHttpService.getTempEmpLookupId(item) || index;
  };

  displayEmployeeTempId(): string {
    const saved = resolveMrfTempPayrollId(this.employeeAssessmentData);
    if (!saved) return 'Enter';
    const current = this.employeeTempId.value;
    if (current && typeof current === 'object') {
      return this.getTempEmpOptionLabel(current);
    }
    return saved;
  }

  displayTempPayrollId(): string {
    return resolveMrfTempPayrollId(this.employeeAssessmentData) || 'Display';
  }

  hasTempPayrollId(): boolean {
    return !!resolveMrfTempPayrollId(this.employeeAssessmentData);
  }

  displayQualifiedPrevChaitanyaId(): string {
    const value = this.qualifiedFieldValue(
      'prevChaitanyaId',
      'previousChaitanyaId',
      'previousEmployeeId',
      'prevEmpId'
    );
    return value || 'Display';
  }

  hasQualifiedPrevChaitanyaId(): boolean {
    return this.displayQualifiedPrevChaitanyaId() !== 'Display';
  }

  get entityDisplayName(): string {
    return (
      this.entityName ||
      this.mrfData?.entityName ||
      this.mrfData?.campusName ||
      'Select Entity'
    );
  }

  get entityFieldValue(): string {
    const name = this.entityDisplayName;
    return name === 'Select Entity' ? '' : name;
  }

  get reasonDescription(): string {
    return String(
      this.mrfData?.reasonDescription ??
        this.mrfData?.hiringReasonDesc ??
        this.mrfData?.reasonDesc ??
        ''
    ).trim();
  }

  get isReasonExit(): boolean {
    return isMrfReasonExit(this.reasonDescription);
  }

  get isReplacement(): boolean {
    const pt = String(this.mrfData?.positionType ?? '').toUpperCase();
    return pt.includes('REPLACEMENT') || pt === 'REPLACEMENT';
  }

  get isNewPosition(): boolean {
    return !this.isReplacement;
  }

  get isRehiring(): boolean {
    return isMrfRehiringFromData(this.mrfData ?? {}) || this.rehiring;
  }

  /** Principal can edit rehiring at Requested or Pending at MIS; MIS/HR are read-only. */
  get canEditRehiringSection(): boolean {
    if (!this.mrfPermissions.principalForm) return false;
    const workflow = normalizeMrfWorkflowStatus(this.getWorkflowStatus());
    return (
      workflow === 'REQUESTED' ||
      workflow === 'PENDING_AT_MIS' ||
      workflow === 'APPROVAL'
    );
  }

  get isNonTeach(): boolean {
    const role = String(this.mrfData?.roleType ?? this.mrfData?.teachingType ?? '').toUpperCase();
    return role.includes('NON TEACH') || role.includes('NON-TEACH');
  }

  /** Exam Taken Yes is allowed only when getMrf testSubject is true. */
  get isTestSubject(): boolean {
    const value =
      this.mrfData?.['testSubject'] ??
      this.mrfData?.['isTestSubject'] ??
      this.mrfData?.['TestSubject'];
    return value === true || value === 1 || value === '1' || String(value ?? '').trim().toLowerCase() === 'true';
  }

  get availableHeadcountDisplay(): string {
    const v = String(this.availableHeadcount ?? '').trim();
    if (!v || v === '—') return '00';
    const n = Number(v);
    if (!Number.isNaN(n)) return String(Math.max(0, Math.floor(n))).padStart(2, '0');
    return v;
  }

  get isExpectedSalaryOverAvailableBudget(): boolean {
    return this.hrHttpService.isMrfExpectedSalaryOverAvailableBudget(
      this.hrHttpService.resolveMrfExpectedSalaryValue(
        this.mrfData ?? {},
        this.expectedSalary.value
      ),
      this.availableBudgetAmount
    );
  }

  formatBudgetAmount(value: number | null): string {
    return this.hrHttpService.formatMrfBudgetAmount(value);
  }

  /** Principal can approve/reject only while request is at principal stage. */
  get showPrincipalApprovalActions(): boolean {
    if (!this.mrfPermissions.principalForm) return false;
    const workflow = normalizeMrfWorkflowStatus(this.getWorkflowStatus());
    return workflow === 'REQUESTED' || workflow === 'APPROVAL';
  }

  get isPendingAtMisStage(): boolean {
    return normalizeMrfWorkflowStatus(this.getWorkflowStatus()) === 'PENDING_AT_MIS';
  }

  get showMisEditMode(): boolean {
    return this.mrfPermissions.misForm && this.isPendingAtMisStage;
  }

  /** Principal updates assessment while request is pending at MIS. */
  get showPrincipalEditMode(): boolean {
    return this.mrfPermissions.principalForm && this.isPendingAtMisStage;
  }

  get showPrincipalActions(): boolean {
    return (
      this.showPrincipalApprovalActions ||
      this.showPrincipalEditMode ||
      this.showMisEditMode
    );
  }

  get canEditQualifiedEmployeeSection(): boolean {
    return this.showPrincipalActions;
  }

  get showQualifiedEmployeeAsReadOnly(): boolean {
    return this.isReadOnlyView;
  }

  /** checkEmployeeByAadhaar runs only at principal (REQUESTED) stage, not MIS edit. */
  get shouldVerifyEmployeeAadhaar(): boolean {
    return this.showPrincipalApprovalActions;
  }

  get isReadOnlyView(): boolean {
    return !this.showPrincipalActions;
  }

  /** Manual entry for qualified employee when exam was not taken. */
  get canEnterNewEmployeeIdentityFields(): boolean {
    return this.canEditQualifiedEmployeeSection && this.isExamTaken('no');
  }

  /** Rehiring section — editable on principal form at Requested / Pending at MIS. */
  get showRehiringSection(): boolean {
    return this.canEditRehiringSection;
  }

  get rehiringDobDisplay(): string {
    return formatMrfDateForDisplay(this.rehiringDob.value) || '';
  }

  get isRehiringFieldsLocked(): boolean {
    return this.rehiringDataLocked;
  }

  get prevChaitanyaIdDisplay(): string {
    const lookupId = this.resolvePrevChaitanyaId() ?? '';
    return lookupId ? this.resolveLeftEmpDisplayLabel(lookupId) : '';
  }

  onRehiringChange(ev: Event): void {
    const el = ev.target as HTMLInputElement;
    if (this.rehiringDataLocked) {
      el.checked = this.rehiring;
      return;
    }
    this.rehiring = el.checked;
    if (!this.rehiring) {
      this.clearAadhaarDrivenRehiring();
    }
  }

  cancel(): void {
    this.router.navigate(['/hrDetails/mrfDashboard']);
  }

  setExamTaken(value: 'yes' | 'no'): void {
    if (value === 'yes' && !this.isTestSubject) {
      return;
    }
    if (this.examTaken === value) {
      return;
    }

    this.stashQualifiedEmployeeForExam(this.examTaken);
    this.examTaken = value;

    const snapshot = value === 'yes' ? this.examTakenYesSnapshot : this.examTakenNoSnapshot;
    if (snapshot) {
      this.restoreQualifiedEmployeeSnapshot(snapshot);
    } else {
      this.clearQualifiedEmployeeDetails();
    }

    this.setPrincipalEditableFieldsEnabled(!this.isReadOnlyView);
  }

  private stashQualifiedEmployeeForExam(mode: ExamTakenMode): void {
    const snapshot = this.captureQualifiedEmployeeSnapshot();
    if (mode === 'yes') {
      this.examTakenYesSnapshot = snapshot;
    } else {
      this.examTakenNoSnapshot = snapshot;
    }
  }

  private seedInitialExamSnapshotIfMissing(): void {
    if (this.examTaken === 'yes' && !this.examTakenYesSnapshot) {
      this.examTakenYesSnapshot = this.captureQualifiedEmployeeSnapshot();
    } else if (this.examTaken === 'no' && !this.examTakenNoSnapshot) {
      this.examTakenNoSnapshot = this.captureQualifiedEmployeeSnapshot();
    }
  }

  private captureQualifiedEmployeeSnapshot(): QualifiedEmployeeExamSnapshot {
    const mrfDataFields: Record<string, unknown> = {};
    for (const key of QUALIFIED_EMPLOYEE_MRF_KEYS) {
      const val = this.employeeAssessmentData[key];
      if (val != null && String(val).trim() !== '') {
        mrfDataFields[key] = val;
      }
    }

    return {
      employeeTempId: this.employeeTempId.value ?? '',
      employeeName: String(this.employeeName.value ?? ''),
      surname: String(this.surname.value ?? ''),
      mobileNo: normalizeMrfMobileDigits(this.mobileNo.value),
      aadhaarCardNo: String(this.aadhaarCardNo.value ?? ''),
      panNo: String(this.panNo.value ?? ''),
      dob: String(this.dob.value ?? ''),
      gender: String(this.gender.value ?? ''),
      expectedSalary: String(this.expectedSalary.value ?? ''),
      lastConfirmedDob: this.lastConfirmedDob,
      empTypeUpdatedToConsultant: this.empTypeUpdatedToConsultant,
      tempEmpOptionsList: [...this.tempEmpOptionsList],
      panPendingFiles: [...this.panPendingFiles],
      aadhaarPendingFiles: [...this.sharedAadhaarPendingFiles],
      examPendingFiles: [...this.examPendingFiles],
      demoVideoPendingFiles: [...this.demoVideoPendingFiles],
      mrfDataFields,
    };
  }

  private restoreQualifiedEmployeeSnapshot(snapshot: QualifiedEmployeeExamSnapshot): void {
    this.stripQualifiedEmployeeFromMrfData();

    this.employeeTempId.setValue(snapshot.employeeTempId, { emitEvent: false });
    this.employeeName.setValue(snapshot.employeeName, { emitEvent: false });
    this.surname.setValue(snapshot.surname, { emitEvent: false });
    this.mobileNo.setValue(snapshot.mobileNo, { emitEvent: false });
    this.aadhaarCardNo.setValue(snapshot.aadhaarCardNo, { emitEvent: false });
    this.panNo.setValue(snapshot.panNo, { emitEvent: false });
    this.dob.setValue(snapshot.dob, { emitEvent: false });
    this.gender.setValue(normalizeMrfGenderValue(snapshot.gender), { emitEvent: false });
    this.expectedSalary.setValue(
      formatMrfAmountWithCommas(normalizeMrfExpectedSalaryDigits(snapshot.expectedSalary)),
      { emitEvent: false }
    );
    this.lastConfirmedDob = snapshot.lastConfirmedDob;
    this.tempEmpOptionsList = [...snapshot.tempEmpOptionsList];
    this.publishTempEmpOptions([...snapshot.tempEmpOptionsList]);

    this.panPendingFiles = [...snapshot.panPendingFiles];
    this.syncSharedAadhaarPendingFiles(snapshot.aadhaarPendingFiles ?? []);
    this.examPendingFiles = [...snapshot.examPendingFiles];
    this.demoVideoPendingFiles = [...snapshot.demoVideoPendingFiles];

    this.employeeAssessmentData = { ...this.employeeAssessmentData, ...snapshot.mrfDataFields };

    this.empTypeUpdatedToConsultant = snapshot.empTypeUpdatedToConsultant;
    if (snapshot.empTypeUpdatedToConsultant) {
      this.applyConsultantEmployeeType();
    } else if (this.originalEmpType) {
      this.mrfData = {
        ...this.mrfData,
        empType: this.originalEmpType,
        employeeType: this.originalEmpType,
        empTypeName: this.originalEmpType,
      };
      this.empTypeUpdatedToConsultant = false;
    }
  }

  private stripQualifiedEmployeeFromMrfData(): void {
    this.mrfData = this.stripQualifiedEmployeeKeysFromRecord(this.mrfData ?? {});
    this.employeeAssessmentData = this.stripQualifiedEmployeeKeysFromRecord(
      this.employeeAssessmentData ?? {}
    );
  }

  private stripQualifiedEmployeeKeysFromRecord(
    data: Record<string, unknown>,
    preserveExamTaken = false
  ): Record<string, unknown> {
    const cleared = { ...data };
    for (const key of QUALIFIED_EMPLOYEE_MRF_KEYS) {
      delete cleared[key];
    }
    if (!preserveExamTaken) {
      delete cleared['examTaken'];
      delete cleared['isExamTaken'];
      delete cleared['examTakenFlag'];
    }
    return cleared;
  }

  private clearQualifiedEmployeeDetails(): void {
    this.employeeTempId.setValue('', { emitEvent: false });
    this.employeeName.setValue('', { emitEvent: false });
    this.surname.setValue('', { emitEvent: false });
    this.mobileNo.setValue('', { emitEvent: false });
    this.aadhaarCardNo.setValue('', { emitEvent: false });
    this.panNo.setValue('', { emitEvent: false });
    this.dob.setValue('', { emitEvent: false });
    this.gender.setValue('', { emitEvent: false });
    this.expectedSalary.setValue('', { emitEvent: false });
    this.lastConfirmedDob = '';
    this.restoreOriginalEmployeeType();

    this.tempEmpOptionsList = [];
    this.publishTempEmpOptions([]);

    this.syncSharedAadhaarPendingFiles([]);
    this.panPendingFiles = [];
    this.examPendingFiles = [];
    this.demoVideoPendingFiles = [];

    this.stripQualifiedEmployeeFromMrfData();
    this.resetPanValidationState();
  }

  onAadhaarCardInput(event: Event): void {
    syncMrfAadhaarControlFromInput(this.aadhaarCardNo, event);
  }

  onPanInput(event: Event): void {
    syncMrfPanControlFromInput(this.panNo, event);
  }

  private setupPanValidation(): void {
    this.subs.add(
      this.panNo.valueChanges
        .pipe(
          map((value) => normalizeMrfPanNo(value).slice(0, MRF_PAN_LENGTH)),
          distinctUntilChanged()
        )
        .subscribe((pan) => {
          if (String(this.panNo.value ?? '') !== pan) {
            this.panNo.setValue(pan, { emitEvent: false });
          }
          this.validatePanWhenComplete(pan);
        })
    );
  }

  private resetPanValidationState(): void {
    this.panValidationError = '';
    this.lastPanAlerted = '';
  }

  private validatePanWhenComplete(pan: string): void {
    if (pan.length !== MRF_PAN_LENGTH) {
      this.resetPanValidationState();
      return;
    }

    const error = getMrfPanValidationError(pan, 'Pan Card No.');
    this.panValidationError = error ?? '';
    if (error && this.lastPanAlerted !== pan) {
      this.lastPanAlerted = pan;
      this.hrAlert.show(error, 'Warning');
    } else if (!error) {
      this.lastPanAlerted = '';
    }
  }

  private queuePanValidation(): void {
    const pan = normalizeMrfPanNo(this.panNo.value);
    if (pan.length !== MRF_PAN_LENGTH) {
      this.resetPanValidationState();
      return;
    }
    this.validatePanWhenComplete(pan);
  }

  private setupAadhaarVerification(): void {
    this.subs.add(
      this.aadhaarCardNo.valueChanges
        .pipe(
          map((value) => normalizeMrfAadhaarDigits(value)),
          distinctUntilChanged(),
          switchMap((digits) => {
            if (digits.length !== MRF_AADHAAR_DIGIT_LENGTH) {
              this.resetAadhaarVerifyState();
              if (this.shouldVerifyEmployeeAadhaar) {
                this.clearAadhaarDrivenRehiring();
              }
              return of(null);
            }
            return this.verifyAadhaarOnCompleteDigits(digits);
          })
        )
        .subscribe()
    );
  }

  private verifyAadhaarOnCompleteDigits(digits: string): Observable<null> {
    if (!this.shouldVerifyEmployeeAadhaar) {
      this.resetAadhaarVerifyState();
      return of(null);
    }

    return this.verifyAadhaarDigits(digits);
  }

  private resetAadhaarVerifyState(): void {
    this.aadhaarAlreadyExists = false;
    this.aadhaarVerifyInProgress = false;
    this.lastAadhaarAlerted = '';
    this.lastAadhaarBlockMessage = '';
    this.lastAadhaarTempPayrollId = '';
    this.blockApproveForMissingExam = false;
    if (this.examTakenLockedByAadhaar) {
      this.examTakenLockedByAadhaar = false;
      this.setPrincipalEditableFieldsEnabled(!this.isReadOnlyView);
    }
  }

  private verifyAadhaarDigits(digits: string): Observable<null> {
    this.aadhaarVerifyInProgress = true;
    this.aadhaarAlreadyExists = false;

    return this.hrHttpService.verifyQualifiedEmployeeAadhaar(digits).pipe(
      finalize(() => {
        this.aadhaarVerifyInProgress = false;
      }),
      map((result) => {
        if (normalizeMrfAadhaarDigits(this.aadhaarCardNo.value) !== digits) {
          return null;
        }
        const blocked = !!(result.alreadyExists || result.blocked);
        this.aadhaarAlreadyExists = blocked;
        this.lastAadhaarBlockMessage = blocked ? String(result.message ?? '').trim() : '';
        this.lastAadhaarTempPayrollId = this.readRawTempPayrollId(result);
        this.blockApproveForMissingExam =
          !blocked && this.isTestSubject && !this.lastAadhaarTempPayrollId;

        if (
          this.blockApproveForMissingExam &&
          this.lastAadhaarAlerted !== digits
        ) {
          this.lastAadhaarAlerted = digits;
          this.hrAlert.show(MrfPrincipalDeanComponent.ATTEMPT_EXAM_MESSAGE, 'Error');
        }

        if (result.employeeData && !result.blocked) {
          this.applyAadhaarEmployeeData(result.employeeData);
        } else {
          this.clearAadhaarDrivenRehiring();
          if (blocked && result.message && this.lastAadhaarAlerted !== digits) {
            this.lastAadhaarAlerted = digits;
            this.hrAlert.show(
              result.message,
              result.blocked ? 'Error' : 'Warning'
            );
          }
        }
        return null;
      }),
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  private applyAadhaarEmployeeData(data: Record<string, unknown>): void {
    const hasExamTempId = !!this.readRawTempPayrollIdFromRecord(data);
    if (!this.isNonTeach && this.isTestSubject && hasExamTempId) {
      this.examTakenLockedByAadhaar = true;
      if (this.examTaken !== 'yes') {
        this.setExamTaken('yes');
      }
    } else if (this.isTestSubject && !hasExamTempId) {
      this.examTakenLockedByAadhaar = false;
      if (this.examTaken === 'yes') {
        this.setExamTaken('no');
      }
    }

    const mapped = this.hrHttpService.mapTempEmpToViewData(data);
    this.bindQualifiedEmployeeDetails(mapped);
    this.employeeTempId.setValue(mapped, { emitEvent: false });
    this.disableAadhaarBoundFields(mapped);
    this.applyRehiringFromAadhaarVerification(mapped);
  }

  private resolvePrevChaitanyaIdFromMapped(mapped: Record<string, unknown>): string {
    return this.pickTempEmpField(
      mapped,
      'prevChaitanyaId',
      'previousChaitanyaId',
      'previousEmployeeId',
      'prevEmpId'
    );
  }

  /** When checkEmployeeByAadhaar returns a previous employee, bind rehiring; otherwise clear it. */
  private applyRehiringFromAadhaarVerification(mapped: Record<string, unknown>): void {
    if (!this.canEditRehiringSection) {
      return;
    }

    const prevChaitanyaId = this.resolvePrevChaitanyaIdFromMapped(mapped);
    if (!prevChaitanyaId) {
      this.clearAadhaarDrivenRehiring();
      return;
    }

    this.unlockRehiringFields();
    this.rehiring = true;
    this.lockRehiringFields();
    const savedAadhar = normalizeMrfAadhaarDigits(
      this.aadhaarCardNo.value ??
        mapped['aadharNo'] ??
        mapped['aadhaarNo'] ??
        mapped['aadhar'] ??
        mapped['aadharCardNo'] ??
        ''
    );
    this.fetchAndBindRehiringEmployee(prevChaitanyaId, {
      ...mapped,
      prevChaitanyaId,
      rehiringPayrollId: prevChaitanyaId,
      ...(savedAadhar ? { aadharNo: savedAadhar, aadhaarNo: savedAadhar, aadhar: savedAadhar } : {}),
    });
  }

  private clearAadhaarDrivenRehiring(): void {
    this.unlockRehiringFields();
    this.rehiring = false;
    this.prevChaitanyaId.setValue('');
    this.rehiringDob.setValue('');
    this.rehireAadharNo.setValue('');
    this.clearRehiringAadhaarUploads();
    this.prevRehiringEmployeeDetails = null;
    this.prevLeftEmpOptionsList = [];
    this.prevLeftEmpOptionsSubject.next([]);
  }

  private bindQualifiedEmployeeDetails(mapped: Record<string, unknown>): void {
    const name = this.pickTempEmpField(
      mapped,
      'employeeName',
      'empName',
      'newEmpName',
      'newEmployeeName',
      'candidateName',
      'name'
    );
    const surname = this.pickTempEmpField(mapped, 'surname', 'employeeSurname', 'lastName');
    const mobile = this.pickTempEmpField(
      mapped,
      'mobileNo',
      'mobileNumber',
      'mobile',
      'phoneNo',
      'contactNo'
    );
    const aadhar = this.pickTempEmpField(
      mapped,
      'aadharNo',
      'aadhaarNo',
      'aadharCardNo',
      'newEmpAadhar',
      'candidateAadhar',
      'aadharNumber'
    );
    const pan = this.pickTempEmpField(
      mapped,
      'panNo',
      'panCardNo',
      'panNumber',
      'pancardNo',
      'candidatePan'
    );
    const dob = this.pickTempEmpField(mapped, 'dob', 'dateOfBirth', 'employeeDob', 'birthDate', 'newEmpDob');
    const gender = this.pickTempEmpField(mapped, 'gender', 'employeeGender', 'sex');
    const expectedSalary = this.pickTempEmpField(
      mapped,
      'expectedSalary',
      'salary',
      'expectedCTC',
      'salaryBudget'
    );
    const tempPayrollId =
      resolveMrfTempPayrollId(mapped) || this.getTempEmpLookupId(mapped);
    const prevChaitanyaId = this.pickTempEmpField(
      mapped,
      'prevChaitanyaId',
      'previousChaitanyaId',
      'previousEmployeeId',
      'prevEmpId'
    );

    this.employeeAssessmentData = {
      ...this.employeeAssessmentData,
      ...mapped,
      employeeName: name,
      newEmpName: name,
      surname,
      mobileNo: mobile,
      aadhaarNo: aadhar,
      newEmpAadhar: aadhar,
      panNo: pan,
      dob,
      gender,
      tempPayrollId,
      employeeTempId: tempPayrollId,
      ...(prevChaitanyaId ? { prevChaitanyaId } : { prevChaitanyaId: '' }),
    };

    if (name) {
      this.employeeName.setValue(name, { emitEvent: false });
    }
    if (surname) {
      this.surname.setValue(surname, { emitEvent: false });
    }
    if (mobile) {
      this.mobileNo.setValue(normalizeMrfMobileDigits(mobile), { emitEvent: false });
    }
    if (aadhar) {
      this.aadhaarCardNo.setValue(aadhar, { emitEvent: false });
    }
    if (pan) {
      this.panNo.setValue(normalizeMrfPanNo(pan), { emitEvent: false });
    }
    if (dob) {
      this.dob.setValue(formatMrfDateForInput(dob), { emitEvent: false });
    }
    if (gender) {
      this.gender.setValue(normalizeMrfGenderValue(gender), { emitEvent: false });
    }
    if (expectedSalary) {
      this.expectedSalary.setValue(
        formatMrfAmountWithCommas(normalizeMrfExpectedSalaryDigits(expectedSalary)),
        {
        emitEvent: false,
      });
    }
  }

  private disableAadhaarBoundFields(mapped: Record<string, unknown>): void {
    const name = this.pickTempEmpField(mapped, 'employeeName', 'empName', 'newEmpName', 'name');
    const surname = this.pickTempEmpField(mapped, 'surname', 'employeeSurname', 'lastName');
    const mobile = this.pickTempEmpField(mapped, 'mobileNo', 'mobileNumber', 'mobile', 'phoneNo');
    const pan = this.pickTempEmpField(mapped, 'panNo', 'panCardNo', 'panNumber');
    const dob = this.pickTempEmpField(mapped, 'dob', 'dateOfBirth', 'employeeDob');
    const gender = this.pickTempEmpField(mapped, 'gender', 'employeeGender', 'sex');
    const tempId = resolveMrfTempPayrollId(mapped) || this.getTempEmpLookupId(mapped);

    if (name) this.employeeName.disable({ emitEvent: false });
    if (surname) this.surname.disable({ emitEvent: false });
    if (mobile) this.mobileNo.disable({ emitEvent: false });
    if (pan) this.panNo.disable({ emitEvent: false });
    if (dob) this.dob.disable({ emitEvent: false });
    if (gender) this.gender.disable({ emitEvent: false });
    if (tempId) this.employeeTempId.disable({ emitEvent: false });
  }

  private queueAadhaarVerification(): void {
    const digits = normalizeMrfAadhaarDigits(this.aadhaarCardNo.value);
    if (digits.length !== MRF_AADHAAR_DIGIT_LENGTH) {
      this.resetAadhaarVerifyState();
      return;
    }
    this.subs.add(this.verifyAadhaarOnCompleteDigits(digits).subscribe());
  }

  onMobileNoInput(event: Event): void {
    syncMrfMobileControlFromInput(this.mobileNo, event);
  }

  onExpectedSalaryInput(event: Event): void {
    syncMrfExpectedSalaryControlFromInput(this.expectedSalary, event);
  }

  isExamTaken(value: 'yes' | 'no'): boolean {
    return this.examTaken === value;
  }

  getUploadFileError(type: MrfPdDocSlot): string {
    return getMrfUploadSizeError(this.uploadFileErrors, type);
  }

  onFilesSelected(event: Event, type: MrfPdDocSlot): void {
    const input = event.target as HTMLInputElement;
    let files = Array.from(input.files ?? []);
    if (!files.length) return;

    const sizeMessage = setMrfUploadSizeError(this.uploadFileErrors, type, files);
    if (sizeMessage) {
      this.hrAlert.show(sizeMessage, 'Warning');
      input.value = '';
      return;
    }

    if (type === 'exam' && !this.isExamTaken('yes')) {
      input.value = '';
      return;
    }

    if (type === 'exam') {
      const invalid = files.filter((f) => !this.isPdfFileName(f));
      if (invalid.length) {
        this.hrAlert.show('Only PDF files are allowed for Exam Copy.', 'Warning');
        input.value = '';
        return;
      }
    }

    clearMrfUploadSizeError(this.uploadFileErrors, type);

    if (type === 'exam' || type === 'aadhaar' || type === 'pan') {
      this.setPendingFiles(type, files.slice(0, 1));
      if (type === 'aadhaar') {
        clearMrfUploadSizeError(this.uploadFileErrors, 'aadhaar');
        this.rehiringAadhaarUploadError = '';
      }
      input.value = '';
      return;
    }

    const current = this.getPendingFiles(type);
    const max = this.getMaxFiles(type);
    const remaining = max - current.length;
    if (remaining <= 0) {
      this.hrAlert.show(`You can attach up to ${max} file(s) for this field.`, 'Warning');
      input.value = '';
      return;
    }

    const toAdd = files.slice(0, remaining);
    if (files.length > remaining) {
      this.hrAlert.show(`Only ${remaining} more file(s) allowed (maximum ${max}).`, 'Warning');
    }

    this.setPendingFiles(type, [...current, ...toAdd]);
    input.value = '';
  }

  removePendingFile(type: MrfPdDocSlot, index: number): void {
    if (type === 'rehiringAadhaar' || type === 'aadhaar') {
      this.syncSharedAadhaarPendingFiles([]);
      clearMrfUploadSizeError(this.uploadFileErrors, 'aadhaar');
      this.rehiringAadhaarUploadError = '';
      return;
    }
    const current = this.getPendingFiles(type);
    this.setPendingFiles(
      type,
      current.filter((_, i) => i !== index)
    );
    clearMrfUploadSizeError(this.uploadFileErrors, type);
  }

  canAddMoreFiles(type: MrfPdDocSlot): boolean {
    if (type === 'aadhaar') {
      return this.canAddMoreSharedAadhaarFiles();
    }
    return this.getPendingFiles(type).length < this.getMaxFiles(type);
  }

  onUploadClick(event: Event, type: MrfPdDocSlot, fileInput: HTMLInputElement): void {
    if (!this.canAddMoreFiles(type)) {
      event.preventDefault();
      return;
    }
    fileInput.click();
  }

  private validateQualifiedEmployeeDetails(): string | null {
    if (!this.showPrincipalActions) {
      return null;
    }

    const mobile = String(this.mobileNo.value ?? '').trim();
    const pan = normalizeMrfPanNo(this.panNo.value);
    const dob = String(this.dob.value ?? '').trim();
    const gender = String(this.gender.value ?? '').trim();

    const mobileError = getMrfMobileValidationError(mobile);
    if (mobileError) return mobileError;
    const panError = getMrfPanValidationError(pan, 'Pan Card No.');
    if (panError) return panError;
    const aadhaarError = getMrfAadhaarValidationError(
      this.aadhaarCardNo.value,
      'Aadhaar Card No.'
    );
    if (aadhaarError) return aadhaarError;
    if (!dob) return 'Please enter DOB.';
    const expectedSalError = getMrfExpectedSalaryValidationError(this.expectedSalary.value);
    if (expectedSalError) return expectedSalError;
    if (!gender) return 'Please select Gender.';

    if (this.isTestSubject && !this.hasAttemptedExamTempPayrollId()) {
      this.blockApproveForMissingExam = true;
      return MrfPrincipalDeanComponent.ATTEMPT_EXAM_MESSAGE;
    }

    if (this.canEnterNewEmployeeIdentityFields) {
      const name = String(this.employeeName.value ?? '').trim();
      if (!name) return 'Please enter Name.';
      const surname = String(this.surname.value ?? '').trim();
      if (!surname) return 'Please enter Surname.';
    }

    if (this.isExamTaken('yes')) {
      if (!this.resolveEmployeeTempId()) {
        return 'Please select Employee Temp Id.';
      }
      if (!this.hasExamCopyPdfReady()) {
        return 'Upload Exam Copy (PDF) is mandatory.';
      }
    }

    const rehiringError = this.validateRehiringDetails();
    if (rehiringError) return rehiringError;

    return null;
  }

  private validateRehiringDetails(): string | null {
    if (!this.rehiring) return null;

    const payrollId = this.resolvePrevChaitanyaId();
    if (!payrollId) {
      return 'Please select a valid Previous Payroll Id for rehiring.';
    }
    if (!String(this.rehiringDob.value ?? '').trim()) {
      return 'Please enter Date of Birth for rehiring.';
    }
    return getMrfAadhaarValidationError(this.rehireAadharNo.value, 'Aadhaar No.');
  }

  submitReview(status: 'APPROVED' | 'REJECTED'): void {
    if (this.isSaving) return;

    const mrfId = this.getMrfFormId();
    if (!mrfId) {
      this.hrAlert.show('MRF id is missing.', 'Error');
      return;
    }

    if (this.aadhaarAlreadyExists) {
      this.hrAlert.show(
        this.lastAadhaarBlockMessage || 'Aadhaar number already exists.',
        'Warning'
      );
      return;
    }

    if (status === 'APPROVED') {
      const validationError = this.validateQualifiedEmployeeDetails();
      if (validationError) {
        this.hrAlert.show(validationError, 'Warning');
      return;
    }

      this.runIfEmployeeAgeAllowed(() => this.executeSubmitReview(status));
      return;
    }

    this.executeSubmitReview(status);
  }

  private executeSubmitReview(status: 'APPROVED' | 'REJECTED'): void {
    const mrfId = this.getMrfFormId();
    if (!mrfId) return;

    this.isSaving = true;
    this.spinner.show();

    const saveChain =
      status === 'APPROVED'
        ? this.buildApproveSaveChain(mrfId)
        : this.buildRejectSaveChain(mrfId);

    saveChain.subscribe({
      next: (res) => {
        this.isSaving = false;
        this.clearPendingFiles();
        this.spinner.hide();
        this.hrAlert.showOnDashboard(
          status === 'APPROVED'
            ? this.hrHttpService.buildSaveEmployeeAssessmentApprovalMessage(res)
            : 'MRF rejected.',
          'Success'
        );
        this.router.navigate(['/hrDetails/mrfDashboard']);
      },
      error: (err: unknown) => {
        this.isSaving = false;
        this.spinner.hide();
        console.error('Principal/Dean review failed', err);
        this.hrAlert.show(
          this.extractApiErrorMessage(err, 'Failed to submit. Please try again.'),
          'Error'
        );
      },
    });
  }

  private extractApiErrorMessage(err: unknown, fallback: string): string {
    const httpErr = err as { error?: unknown; message?: string } | null;
    let body = httpErr?.error;

    if (typeof body === 'string') {
      const text = body.trim();
      if (!text) {
        return fallback;
      }
      try {
        body = JSON.parse(text) as unknown;
      } catch {
        return text;
      }
    }

    if (body && typeof body === 'object') {
      const msg = String((body as Record<string, unknown>)['message'] ?? '').trim();
      if (msg) {
        return msg;
      }
    }

    return fallback;
  }

  hasUploadedExamCopyPdf(): boolean {
    return this.examDocuments.some((doc) => this.isPdfFile(doc));
  }

  hasExamCopyPdfReady(): boolean {
    if (!this.isExamTaken('yes')) {
      return true;
    }
    const hasPendingPdf = this.examPendingFiles.some((f) => this.isPdfFileName(f));
    return this.hasUploadedExamCopyPdf() || hasPendingPdf;
  }

  isPdfFileName(file: File): boolean {
    const name = file.name.toLowerCase();
    return name.endsWith('.pdf') || String(file.type ?? '').includes('pdf');
  }

  private buildApproveSaveChain(mrfId: string): Observable<unknown> {
    const uploads: Observable<unknown>[] = [];

    if (this.examPendingFiles.length && this.isExamTaken('yes')) {
      uploads.push(
        this.hrHttpService.uploadMrfDocuments(
          mrfId,
          this.examPendingFiles,
          MRF_DOCUMENT_TYPE.EXAM_COPY
        )
      );
    }

    if (this.panPendingFiles.length) {
      uploads.push(
        this.hrHttpService.uploadMrfDocuments(
          mrfId,
          this.panPendingFiles,
          MRF_DOCUMENT_TYPE.PAN
        )
      );
    }

    const sharedAadhaarPending = this.sharedAadhaarPendingFiles;
    if (sharedAadhaarPending.length) {
      uploads.push(
        this.hrHttpService.uploadMrfDocuments(
          mrfId,
          sharedAadhaarPending,
          MRF_DOCUMENT_TYPE.AADHAR
        )
      );
    }

    if (this.demoVideoPendingFiles.length) {
      uploads.push(this.hrHttpService.uploadMrfDocuments(mrfId, this.demoVideoPendingFiles));
    }

    const uploads$ = uploads.length
      ? from(uploads).pipe(concatMap((upload) => upload))
      : of(undefined);

    return uploads$.pipe(
      switchMap(() => {
        let chain$: Observable<unknown> = of(undefined);

        if (this.rehiring) {
          chain$ = this.hrHttpService.saveMrfForm(this.buildRehireMrfFormPayload(mrfId));
        } else if (this.empTypeUpdatedToConsultant) {
          chain$ = this.hrHttpService.saveMrfForm(this.buildMrfFormPayloadForSave(mrfId));
        }

        return chain$.pipe(
      switchMap(() =>
        this.hrHttpService.saveEmployeeAssessment(this.buildEmployeeAssessmentPayload(mrfId))
      )
        );
      })
    );
  }

  private buildRejectSaveChain(mrfId: string): Observable<unknown> {
    return this.hrHttpService.savePrincipalDeanReview({
      mrfFormId: Number(mrfId),
      mrfFormStatus: 'REJECTED',
    });
  }

  getDocumentName(doc: MrfDocumentView): string {
    return this.hrHttpService.getMrfDocumentViewName(doc);
  }

  getDocumentUrl(doc: MrfDocumentView): string {
    return this.hrHttpService.getMrfDocumentViewUrl(doc);
  }

  canDownloadDocument(doc: MrfDocumentView): boolean {
    return this.hrHttpService.canDownloadMrfDocument(doc);
  }

  onDocumentDownload(doc: MrfDocumentView): void {
    this.spinner.show();
    this.hrHttpService
      .triggerMrfDocumentDownload(doc)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        error: (err) => {
          console.error(err);
          this.hrAlert.show('Failed to download document.', 'Error');
        },
      });
  }

  getDocumentIconLabel(doc: MrfDocumentView): string {
    return this.hrHttpService.getMrfDocumentIconLabel(doc);
  }

  isImageFile(doc: any): boolean {
    return this.hrHttpService.isMrfImageDocument(doc);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.revokeDocumentBlobUrls();
    this.revokeRehiringAadhaarDocumentUrls();
    this.spinner.hide();
  }

  private revokeDocumentBlobUrls(): void {
    for (const doc of this.documents) {
      if (doc?.blobUrl) {
        URL.revokeObjectURL(doc.blobUrl);
      }
    }
  }

  isPdfFile(doc: any): boolean {
    const name = this.getDocumentName(doc).toLowerCase();
    return name.endsWith('.pdf') || String(doc?.mimeType ?? '').includes('pdf');
  }

  isVideoFile(doc: any): boolean {
    const name = this.getDocumentName(doc).toLowerCase();
    return (
      /\.(mov|mp4|avi|webm|mkv)$/.test(name) ||
      String(doc?.mimeType ?? '').includes('video')
    );
  }

  isPendingVideo(file: File): boolean {
    return /\.(mov|mp4|avi|webm|mkv)$/i.test(file.name) || String(file.type ?? '').includes('video');
  }

  display(...keys: string[]): string {
    const v = this.fieldValue(...keys);
    return v || '—';
  }

  /** Figma placeholder when API value is missing (e.g. "Display", "Select Entity"). */
  displayPlaceholder(placeholder: string, ...keys: string[]): string {
    const v = this.fieldValue(...keys);
    return v || placeholder;
  }

  displayRehiringEmployeeName(): string {
    return (
      this.fieldValue('prevEmpName', 'newEmpName', 'newEmployeeName', 'employeeName', 'candidateName') ||
      'Display'
    );
  }

  displayLastWorkingDate(...keys: string[]): string {
    return formatMrfDateForDisplay(
      this.fieldValue(
      ...(keys.length ? keys : ['lastWorkingDate', 'lastWorkingDay', 'lwd'])
      )
    );
  }

  displayDob(): string {
    return formatMrfDateForDisplay(
      this.qualifiedFieldValue('dob', 'dateOfBirth', 'employeeDob', 'newEmpDob')
    );
  }

  qualifiedDisplay(...keys: string[]): string {
    const v = this.qualifiedFieldValue(...keys);
    return v || '—';
  }

  qualifiedDisplayPlaceholder(placeholder: string, ...keys: string[]): string {
    const v = this.qualifiedFieldValue(...keys);
    if (!v) {
      return placeholder;
    }
    if (this.isQualifiedAmountField(...keys)) {
      return formatMrfAmountForDisplay(v, placeholder);
    }
    return v;
  }

  private isQualifiedAmountField(...keys: string[]): boolean {
    const amountKeys = new Set([
      'expectedSalary',
      'salaryBudget',
      'salary',
      'expectedCTC',
      'expectedSal',
    ]);
    return keys.some((key) => amountKeys.has(key));
  }

  qualifiedHasField(...keys: string[]): boolean {
    return !!this.qualifiedFieldValue(...keys);
  }

  hasField(...keys: string[]): boolean {
    return !!this.fieldValue(...keys);
  }

  hasReportingManager(): boolean {
    return !!this.hrHttpService.getMrfReportingManagerDisplayFromData(
      this.mrfData ?? {},
      this.reportingManagersList
    );
  }

  displayReportingManager(): string {
    const label = this.hrHttpService.getMrfReportingManagerDisplayFromData(
      this.mrfData ?? {},
      this.reportingManagersList
    );
    return label || 'Display';
  }

  hasYearsOfExperience(): boolean {
    return resolveMrfYearsOfExperience(this.mrfData ?? {}) !== '';
  }

  displayYearsOfExperience(): string {
    const value = resolveMrfYearsOfExperience(this.mrfData ?? {});
    return value || 'Display';
  }

  displayQualifiedEmployeeName(): string {
    const name = resolveMrfQualifiedEmployeeName(this.employeeAssessmentData, this.isExamTaken('yes'));
    return name || 'Display';
  }

  displaySurname(): string {
    const value = resolveMrfSurname({
      ...this.employeeAssessmentData,
      surname: this.surname.value,
      employeeSurname: this.surname.value,
    });
    return value || 'Display';
  }

  hasSurname(): boolean {
    return !!resolveMrfSurname({
      ...this.employeeAssessmentData,
      surname: this.surname.value,
      employeeSurname: this.surname.value,
    });
  }

  hasQualifiedEmployeeName(): boolean {
    return !!resolveMrfQualifiedEmployeeName(this.employeeAssessmentData, this.isExamTaken('yes'));
  }

  /** First non-empty qualified-employee value from getEmployeeAssessment only. */
  qualifiedFieldValue(...keys: string[]): string {
    for (const key of keys) {
      const val = this.employeeAssessmentData[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  }

  /** First non-empty value for input binding (empty string when missing). */
  fieldValue(...keys: string[]): string {
    for (const key of keys) {
      const val = this.mrfData?.[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  }

  /** Loads MRF detail and saved principal assessment (mobile, pan, exam, documents). */
  private loadMrfFormData(): void {
    const mrfId = this.getMrfFormId();
    if (!mrfId) return;

    this.spinner.show();
    this.employeeAssessmentErrored = false;
    forkJoin({
      requests: this.hrHttpService.getMrfRequests(mrfId),
      assessment: this.hrHttpService.getEmployeeAssessment(mrfId).pipe(
        catchError((err) => {
          console.error('getEmployeeAssessment failed', err);
          this.employeeAssessmentErrored = true;
          return of(null);
        })
      ),
      documents: this.hrHttpService
        .getMrfDocumentsByMrfFormId(mrfId)
        .pipe(catchError(() => of([] as MrfDocumentView[]))),
      roles: this.hrHttpService.getUserRoles().pipe(catchError(() => of(null))),
    })
      .pipe(
        switchMap(({ requests, assessment, roles, documents }) => {
          const data = this.hrHttpService.normalizeMrfRequestsResponse(requests);
          const campusId = (data?.['campusId'] ?? null) as string | number | null;
          return this.hrHttpService.getMrfReportingManagers(campusId).pipe(
            catchError(() => of(null)),
            map((reportingManagers) => ({
              requests,
              assessment,
              roles,
              documents,
              reportingManagers,
            }))
          );
        })
      )
      .subscribe({
      next: ({ requests, assessment, reportingManagers, roles, documents }) => {
        try {
          if (roles != null) {
            this.mrfPermissions = this.hrHttpService.setMrfHrmsPermissionsFromResponse(roles);
          }
          this.reportingManagersList =
            this.hrHttpService.normalizeMrfReportingManagerList(reportingManagers);
          let data = this.stripQualifiedEmployeeKeysFromRecord(
            this.hrHttpService.normalizeMrfRequestsResponse(requests),
            true
          );
          this.employeeAssessmentRaw = assessment;
          this.employeeAssessmentData = this.hrHttpService.mapEmployeeAssessmentToViewData(
            this.hrHttpService.parseEmployeeAssessmentResponse(assessment)
          );
          this.mrfData = data;
          this.originalEmpType = String(data?.['empType'] ?? data?.['employeeType'] ?? '').trim();
          this.originalReplacementEmpId = this.parseReplacementEmpIdFromGetMrf(data);
          this.empTypeUpdatedToConsultant = false;
        this.entityName =
          String(
            this.mrfData?.entityName ??
              this.mrfData?.entity ??
              this.mrfData?.campusEntity ??
              this.mrfData?.campusName ??
              ''
          );
        const headcount = Number(this.mrfData?.noOfHeadcount ?? this.mrfData?.headcount ?? 1);
        this.noOfHeadcount = String(headcount || 1);
          this.applyPrincipalFormFields();
          const hideSpinner = () => this.spinner.hide();
          this.seedQualifiedEmployeeTempIdFromAssessment(() =>
            this.loadQualifiedEmployeeDocuments(() =>
              this.loadHeadCountFromData(() =>
                this.loadAvailableBudgetFromData(() =>
                  this.loadReplacementEmployeeDetails(hideSpinner)
                )
              )
            , documents)
          );
        } catch (err) {
          this.spinner.hide();
          console.error(err);
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
      },
    });
  }

  private applyPrincipalFormFields(): void {
    this.initExamTakenFromData();
        this.mobileNo.setValue(
      normalizeMrfMobileDigits(
        this.qualifiedFieldValue(
            'mobileNo',
            'mobileNumber',
            'candidateMobile',
            'mobile',
            'phoneNo',
            'contactNo'
          )
      ),
      { emitEvent: false }
        );
        this.panNo.setValue(
      normalizeMrfPanNo(
        this.qualifiedFieldValue(
            'panNo',
            'panCardNo',
            'candidatePan',
            'panNumber',
            'panCardNumber',
            'pancardNo'
        )
      )
    );
    this.employeeName.setValue(this.resolveQualifiedEmployeeNameForForm());
    this.surname.setValue(resolveMrfSurname(this.employeeAssessmentData), { emitEvent: false });
    this.aadhaarCardNo.setValue(
      this.qualifiedFieldValue('newEmpAadhar', 'aadharCardNo', 'candidateAadhar', 'aadharNo', 'aadhaarNo')
    );
    this.gender.setValue(
      normalizeMrfGenderValue(this.qualifiedFieldValue('gender', 'employeeGender', 'sex'))
    );
    const initialDob = formatMrfDateForInput(
      this.qualifiedFieldValue('dob', 'dateOfBirth', 'employeeDob', 'newEmpDob')
    );
    this.dob.setValue(initialDob, { emitEvent: false });
    this.lastConfirmedDob = initialDob;
    this.expectedSalary.setValue(
      formatMrfAmountWithCommas(
        normalizeMrfExpectedSalaryDigits(
          this.qualifiedFieldValue('expectedSalary', 'salaryBudget', 'salary', 'expectedCTC')
        )
      )
    );
    this.setPrincipalEditableFieldsEnabled(!this.isReadOnlyView);
    this.seedInitialExamSnapshotIfMissing();
    this.bindRehiringFromData();
    this.queueAadhaarVerification();
    this.queuePanValidation();
  }

  displayGender(): string {
    const value = normalizeMrfGenderValue(
      this.qualifiedFieldValue('gender', 'employeeGender', 'sex') || this.gender.value
    );
    return value || 'Select';
  }

  displayRehiringAadharNo(): string {
    const value = resolveMrfRehiringAadharNo(this.mrfData ?? {});
    return value || 'Display';
  }

  hasRehiringAadharNo(): boolean {
    return !!resolveMrfRehiringAadharNo(this.mrfData ?? {});
  }

  hasGender(): boolean {
    return !!normalizeMrfGenderValue(
      this.qualifiedFieldValue('gender', 'employeeGender', 'sex') || this.gender.value
    );
  }

  private resolveQualifiedEmployeeNameForForm(): string {
    return resolveMrfQualifiedEmployeeName(this.employeeAssessmentData, this.isExamTaken('yes'));
  }

  private setupTempEmpSearch(): void {
    this.subs.add(
      this.employeeTempId.valueChanges
        .pipe(
          startWith(''),
          debounceTime(300),
          map((value) => this.getTempEmpSearchText(value)),
          distinctUntilChanged(),
          switchMap((search) => {
            if (search.length < MrfPrincipalDeanComponent.TEMP_EMP_MIN_SEARCH_LENGTH) {
              const options = search
                ? this.filterTempEmpOptions(search, this.tempEmpOptionsList)
                : [];
              return of(this.publishTempEmpOptions(options));
            }
            return this.fetchTempEmpOptions(search);
          })
        )
        .subscribe()
    );
  }

  private setupDobAgeValidation(): void {
    this.subs.add(
      this.dob.valueChanges
        .pipe(
          debounceTime(250),
          filter(() => this.canEditQualifiedEmployeeSection),
          distinctUntilChanged(),
          switchMap((value) => this.validateEmployeeAge(value, { revertOnNo: true }))
        )
        .subscribe()
    );
  }

  private runIfEmployeeAgeAllowed(action: () => void): void {
    this.validateEmployeeAge(this.dob.value, { revertOnNo: false }).subscribe((allowed) => {
      if (allowed) action();
    });
  }

  private validateEmployeeAge(
    dobValue: unknown,
    options?: { revertOnNo?: boolean }
  ): Observable<boolean> {
    const dobText = String(dobValue ?? '').trim();
    if (!dobText) {
      this.lastConfirmedDob = '';
      this.restoreOriginalEmployeeType();
      return of(true);
    }

    const age = this.hrHttpService.calculateEmployeeAge(dobText);
    if (age == null || age <= MrfPrincipalDeanComponent.MAX_EMPLOYEE_AGE) {
      this.lastConfirmedDob = dobText;
      this.restoreOriginalEmployeeType();
      return of(true);
    }

    if (dobText === this.lastConfirmedDob) {
      return of(true);
    }

    if (this.ageDialogOpen) {
      return of(false);
    }

    this.ageDialogOpen = true;
    return this.hrAlert
      .confirm(
        `Employee age is ${age} years, which is greater than ${MrfPrincipalDeanComponent.MAX_EMPLOYEE_AGE}. Employee type will be changed to ${MrfPrincipalDeanComponent.CONSULTANT_EMPLOYEE_TYPE}. Do you want to continue?`,
        { title: 'Age Warning' }
      )
      .pipe(
        tap((confirmed) => {
          this.ageDialogOpen = false;
          if (confirmed) {
            this.lastConfirmedDob = dobText;
            this.applyConsultantEmployeeType();
          } else if (options?.revertOnNo !== false) {
            this.dob.setValue(this.lastConfirmedDob, { emitEvent: false });
          }
        })
      );
  }

  private applyConsultantEmployeeType(): void {
    if (!this.originalEmpType) {
      this.originalEmpType = this.fieldValue('empType', 'employeeType', 'empTypeName');
    }
    const alreadyConsultant =
      this.originalEmpType.toLowerCase() ===
      MrfPrincipalDeanComponent.CONSULTANT_EMPLOYEE_TYPE.toLowerCase();
    this.mrfData = {
      ...this.mrfData,
      empType: MrfPrincipalDeanComponent.CONSULTANT_EMPLOYEE_TYPE,
      employeeType: MrfPrincipalDeanComponent.CONSULTANT_EMPLOYEE_TYPE,
      empTypeName: MrfPrincipalDeanComponent.CONSULTANT_EMPLOYEE_TYPE,
    };
    this.empTypeUpdatedToConsultant = !alreadyConsultant;
  }

  private restoreOriginalEmployeeType(): void {
    if (!this.empTypeUpdatedToConsultant || !this.originalEmpType) {
      return;
    }
    this.mrfData = {
      ...this.mrfData,
      empType: this.originalEmpType,
      employeeType: this.originalEmpType,
      empTypeName: this.originalEmpType,
    };
    this.empTypeUpdatedToConsultant = false;
  }

  private buildMrfFormPayloadForSave(mrfId: string): SaveMrfFormPayload {
    const data = this.mrfData ?? {};
    const positionType = String(data['positionType'] ?? '')
      .toUpperCase()
      .includes('REPLACEMENT')
      ? 'REPLACEMENT'
      : 'NEW_POSITION';

    return {
      mrfFormId: Number(mrfId),
      campusId: Number(data['campusId']) || 0,
      roleType: String(data['roleType'] ?? data['teachingType'] ?? ''),
      empType: String(
        data['empType'] ?? data['employeeType'] ?? MrfPrincipalDeanComponent.CONSULTANT_EMPLOYEE_TYPE
      ),
      deptId: Number(data['deptId']) || 0,
      desigId: Number(data['desigId']) || 0,
      subId: Number(data['subId']) || 0,
      workLoad: Number(data['workLoad'] ?? data['workload']) || 0,
      positionType,
      hiringReason: String(data['hiringReason'] ?? ''),
      reasonDescription: String(data['reasonDescription'] ?? ''),
      replacementEmpId: this.resolveReplacementEmpIdForSave(data),
      isReHiring: !!(data['isReHiring'] ?? data['isRehiring']),
      prevChaitanyaId:
        data['prevChaitanyaId'] != null && String(data['prevChaitanyaId']).trim() !== ''
          ? Number(data['prevChaitanyaId'])
          : null,
      aadharNo: String(data['aadharNo'] ?? data['aadhaarNo'] ?? ''),
      qualificationId: Number(data['qualificationId']) || 0,
      additionalQualification: String(data['additionalQualification'] ?? ''),
      yearsOfExp: String(data['yearsOfExp'] ?? ''),
      skills: String(data['skills'] ?? ''),
      reportingManagerId: Number(data['reportingManagerId']) || 0,
      dob: String(data['dob'] ?? ''),
      rehiringPayrollId: this.hrHttpService.resolveMrfRehiringPayrollId(data),
    };
  }

  private toPayloadIdOrNull(value: number): number | null {
    return value === 0 ? null : value;
  }

  private buildRehireMrfFormPayload(mrfId: string): SaveMrfFormPayload {
    const dob = this.rehiring ? formatMrfDateForPayload(this.rehiringDob.value) : '';

    return {
      ...this.buildMrfFormPayloadForSave(mrfId),
      isReHiring: this.rehiring,
      prevChaitanyaId: this.toPayloadIdOrNull(this.resolvePrevChaitanyaNumericId()),
      aadharNo: normalizeMrfAadhaarDigits(this.rehireAadharNo.value),
      dob,
      rehiringPayrollId: this.resolveRehiringPayrollId(),
    };
  }

  displayPrevLeftEmpLabel = (item: LeftEmpControlValue | null): string => {
    if (!item) return '';
    if (typeof item === 'string') {
      return this.resolveLeftEmpDisplayLabel(item);
    }
    return this.getLeftEmpOptionLabel(item);
  };

  onPrevLeftEmpSelected(event: MatAutocompleteSelectedEvent): void {
    if (this.rehiringDataLocked) {
      return;
    }
    const item = event.option.value as Record<string, unknown>;
    if (!item || typeof item !== 'object') return;
    const lookupId = this.getLeftEmpLookupId(item);
    if (lookupId) {
      this.upsertLeftEmpOption(lookupId, item);
    }
    this.applyPrevRehiringEmpDetails(item);
  }

  refreshRehiringLeftEmpAutocomplete(): void {
    if (this.rehiringDataLocked) {
      return;
    }
    const search = this.getLeftEmpSearchText(this.prevChaitanyaId.value);
    if (!search) {
      this.publishLeftEmpOptions();
      return;
    }
    if (search.length < MrfPrincipalDeanComponent.LEFT_EMP_MIN_SEARCH_LENGTH) {
      this.publishLeftEmpOptions(
        search ? this.filterLeftEmpOptions(search, this.prevLeftEmpOptionsList) : []
      );
      return;
    }
    this.subs.add(this.fetchRehiringLeftEmpOptions(search).subscribe());
  }

  trackByLeftEmpId = (index: number, item: Record<string, unknown>): string | number => {
    return this.getLeftEmpLookupId(item) || index;
  };

  getLeftEmpOptionLabel(item: Record<string, unknown>): string {
    const payroll = this.getLeftEmpLookupId(item);
    const name = this.pickLeftEmpField(item, 'empName', 'employeeName', 'name', 'userName');
    const mobile = this.pickLeftEmpField(item, 'mobileNo', 'mobileNumber', 'mobile', 'phoneNo');
    const aadhar = normalizeMrfAadhaarDigits(
      item['aadharNo'] ?? item['aadhaarNo'] ?? item['aadharCardNo'] ?? ''
    );
    const parts = [payroll, name, mobile, aadhar].filter((part) => !!part);
    return parts.join(' | ') || payroll || name || 'Employee';
  }

  get sharedAadhaarDocuments(): MrfDocumentView[] {
    return this.hrHttpService.mergeMrfDocumentLists(
      this.aadhaarDocuments,
      this.rehireAadhaarDocuments
    );
  }

  get sharedAadhaarPendingFiles(): File[] {
    return this.aadhaarPendingFiles.length
      ? this.aadhaarPendingFiles
      : this.rehiringAadhaarPendingFiles;
  }

  hasSharedAadhaarSavedOrPending(): boolean {
    return this.sharedAadhaarDocuments.length > 0 || this.sharedAadhaarPendingFiles.length > 0;
  }

  canAddMoreSharedAadhaarFiles(): boolean {
    return !this.hasSharedAadhaarSavedOrPending();
  }

  canAddMoreRehiringAadhaarFiles(): boolean {
    return this.canAddMoreSharedAadhaarFiles();
  }

  onRehiringAadhaarAttachClick(event: Event, fileInput: HTMLInputElement): void {
    if (!this.canAddMoreSharedAadhaarFiles()) {
      event.preventDefault();
      return;
    }
    fileInput.click();
  }

  onRehiringAadhaarFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;

    const sizeMessage = getMrfUploadFileSizeValidationMessage(files);
    this.rehiringAadhaarUploadError = sizeMessage ?? '';
    if (sizeMessage) {
      this.hrAlert.show(sizeMessage, 'Warning');
      input.value = '';
      return;
    }

    if (!this.canAddMoreSharedAadhaarFiles()) {
      this.hrAlert.show(
        `You can upload up to ${this.maxRehiringAadhaarFiles} Aadhaar file.`,
        'Warning'
      );
      input.value = '';
      return;
    }

    this.rehiringAadhaarUploadError = '';
    clearMrfUploadSizeError(this.uploadFileErrors, 'aadhaar');
    this.syncSharedAadhaarPendingFiles(files.slice(0, this.maxRehiringAadhaarFiles));
    input.value = '';
  }

  onRehireAadhaarInput(event: Event): void {
    syncMrfAadhaarControlFromInput(this.rehireAadharNo, event);
  }

  private setupRehiringLeftEmpSearch(): void {
    this.subs.add(this.buildRehiringLeftEmpSearchStream().subscribe());
  }

  private buildRehiringLeftEmpSearchStream(): Observable<Record<string, unknown>[]> {
    return this.prevChaitanyaId.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      map((value) => this.getLeftEmpSearchText(value)),
      distinctUntilChanged(),
      switchMap((search) => {
        if (this.rehiringDataLocked) {
          return of(this.publishLeftEmpOptions());
        }
        if (search.length < MrfPrincipalDeanComponent.LEFT_EMP_MIN_SEARCH_LENGTH) {
          const options = search
            ? this.filterLeftEmpOptions(search, this.prevLeftEmpOptionsList)
            : [];
          return of(this.publishLeftEmpOptions(options));
        }
        return this.fetchRehiringLeftEmpOptions(search);
      })
    );
  }

  private fetchRehiringLeftEmpOptions(search: string): Observable<Record<string, unknown>[]> {
    return this.hrHttpService.searchLeftEmployees(search).pipe(
      map((res) => this.hrHttpService.normalizeLeftEmpSearchResults(res)),
      map((list) => this.mergeLeftEmpOptions(this.prevLeftEmpOptionsList, list)),
      map((list) => this.publishLeftEmpOptions(list)),
      catchError((err) => {
        console.error('LeftEmpSearch failed', err);
        const fallback = this.filterLeftEmpOptions(search, this.prevLeftEmpOptionsList);
        return of(this.publishLeftEmpOptions(fallback));
      })
    );
  }

  private publishLeftEmpOptions(list: Record<string, unknown>[] = this.prevLeftEmpOptionsList): Record<string, unknown>[] {
    this.prevLeftEmpOptionsList = list;
    this.prevLeftEmpOptionsSubject.next(list);
    return list;
  }

  private mergeLeftEmpOptions(
    existing: Record<string, unknown>[],
    incoming: Record<string, unknown>[]
  ): Record<string, unknown>[] {
    const merged = [...incoming];
    for (const item of existing) {
      const id = this.getLeftEmpLookupId(item);
      if (!id) continue;
      const duplicate = merged.some(
        (row) => this.getLeftEmpLookupId(row).toLowerCase() === id.toLowerCase()
      );
      if (!duplicate) merged.push(item);
    }
    return merged;
  }

  private filterLeftEmpOptions(
    search: string,
    list: Record<string, unknown>[]
  ): Record<string, unknown>[] {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return list.filter((item) => {
      const haystack = [
        this.getLeftEmpLookupId(item),
        this.pickLeftEmpField(item, 'empName', 'employeeName', 'name', 'userName'),
        this.pickLeftEmpField(item, 'mobileNo', 'mobileNumber', 'mobile', 'phoneNo'),
        this.pickLeftEmpField(item, 'aadharNo', 'aadhaarNo', 'aadharCardNo'),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  private getLeftEmpSearchText(value: unknown): string {
    if (value && typeof value === 'object') {
      return this.getLeftEmpLookupId(value as Record<string, unknown>);
    }
    return String(value ?? '').trim();
  }

  private resolveLeftEmpDisplayLabel(value: string): string {
    const id = value.split('|')[0]?.trim() || value.trim();
    if (!id) return '';
    const match =
      this.prevLeftEmpOptionsList.find(
        (row) => this.getLeftEmpLookupId(row).toLowerCase() === id.toLowerCase()
      ) ??
      (this.prevRehiringEmployeeDetails &&
      this.getLeftEmpLookupId(this.prevRehiringEmployeeDetails).toLowerCase() === id.toLowerCase()
        ? this.prevRehiringEmployeeDetails
        : null);
    return match ? this.getLeftEmpOptionLabel(match) : value;
  }

  private getLeftEmpLookupId(data: Record<string, unknown>): string {
    return (
      this.pickLeftEmpField(data, 'payrollId', 'payRollId') ||
      this.pickLeftEmpField(data, 'employeeId', 'empId', 'uniqueId', 'id')
    );
  }

  private pickLeftEmpField(item: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
      const val = item[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  }

  private upsertLeftEmpOption(lookupId: string, item: Record<string, unknown>): void {
    const mapped = this.hrHttpService.mapLeftEmpSearchToViewData(item, lookupId);
    const merged = this.mergeLeftEmpOptions(this.prevLeftEmpOptionsList, [mapped]);
    this.publishLeftEmpOptions(merged);
  }

  private applyPrevRehiringEmpDetails(
    data: Record<string, unknown>,
    options?: { preserveAadhar?: string }
  ): void {
    const lookupId = this.getLeftEmpLookupId(data);
    const mapped = this.hrHttpService.mapLeftEmpSearchToViewData(data, lookupId);
    this.prevRehiringEmployeeDetails = mapped;
    if (lookupId) {
      this.upsertLeftEmpOption(lookupId, mapped);
    }
    this.prevChaitanyaId.setValue(lookupId, { emitEvent: false });

    const aadhar = resolveMrfRehiringAadharPreferringSaved(
      options?.preserveAadhar ? { aadharNo: options.preserveAadhar } : (this.mrfData ?? {}),
      normalizeMrfAadhaarDigits(
        mapped['aadharNo'] ?? mapped['aadhaarNo'] ?? mapped['aadharCardNo'] ?? ''
      )
    );
    if (aadhar) {
      this.rehireAadharNo.setValue(aadhar, { emitEvent: false });
    }
    this.setRehiringDobFromData(mapped);
    if (lookupId) {
      this.lockRehiringFields();
    }
  }

  private lockRehiringFields(): void {
    this.rehiringDataLocked = true;
  }

  private unlockRehiringFields(): void {
    this.rehiringDataLocked = false;
  }

  private setRehiringDobFromData(data: Record<string, unknown>): void {
    const dob = formatMrfDateForInput(
      this.pickLeftEmpField(data, 'rehiringDob', 'dob', 'dateOfBirth', 'employeeDob', 'birthDate')
    );
    this.rehiringDob.setValue(dob, { emitEvent: false });
  }

  private resolvePrevChaitanyaId(): string | null {
    const fromDetails = this.getLeftEmpLookupId(this.prevRehiringEmployeeDetails ?? {});
    if (fromDetails) return fromDetails;
    const typed = String(this.prevChaitanyaId.value ?? '').trim();
    if (!typed) return null;
    return typed.split('|')[0]?.trim() || typed;
  }

  private resolvePrevChaitanyaNumericId(): number {
    const details = (this.prevRehiringEmployeeDetails ?? {}) as Record<string, unknown>;
    return this.toPayloadNumber(
      details['uniqueId'] ??
        details['empId'] ??
        details['employeeId'] ??
        details['prevChaitanyaId'] ??
        details['id'] ??
        this.resolvePrevChaitanyaId()
    );
  }

  private resolveRehiringPayrollId(): string {
    if (!this.rehiring) return '';
    const details = (this.prevRehiringEmployeeDetails ?? {}) as Record<string, unknown>;
    return (
      this.pickLeftEmpField(details, 'payrollId', 'payRollId') ||
      this.resolvePrevChaitanyaId() ||
      ''
    );
  }

  private toPayloadNumber(value: unknown): number {
    if (value == null || value === '') return 0;
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
  }

  private bindRehiringFromData(): void {
    const data = this.mrfData ?? {};
    this.rehiring = isMrfRehiringFromData(data);

    const payrollId = this.hrHttpService.resolveMrfRehiringPayrollId(data);
    const aadhar = resolveMrfRehiringAadharNo(data);

    if (aadhar) {
      this.rehireAadharNo.setValue(aadhar, { emitEvent: false });
    }
    if (this.rehiring) {
      this.setRehiringDobFromData(data);
    }

    if (!this.canEditRehiringSection) {
      return;
    }

    if (this.rehiring && (payrollId || aadhar)) {
      if (payrollId) {
        this.lockRehiringFields();
      }
      this.fetchAndBindRehiringEmployee(payrollId, data);
    }
  }

  private fetchAndBindRehiringEmployee(
    payrollId: string,
    data: Record<string, unknown>,
    onComplete?: () => void
  ): void {
    const normalizedPayrollId = String(payrollId ?? '').trim();
    const savedAadhar = resolveMrfRehiringAadharNo(data);
    if (!normalizedPayrollId && !savedAadhar) {
          onComplete?.();
      return;
    }

    const boundId = this.resolvePrevChaitanyaId();
    if (normalizedPayrollId && boundId === normalizedPayrollId && this.prevRehiringEmployeeDetails) {
          onComplete?.();
      return;
    }

    const lookupKey = normalizedPayrollId || savedAadhar;
    const savedOption = this.buildLeftEmpOptionFromData(lookupKey, {
      ...data,
      ...(normalizedPayrollId
        ? { rehiringPayrollId: normalizedPayrollId, prevChaitanyaId: normalizedPayrollId }
        : {}),
    });

    this.hrHttpService
      .searchLeftEmployees(normalizedPayrollId, { aadharNo: savedAadhar || undefined })
      .subscribe({
      next: (res) => {
        const apiList = this.hrHttpService.normalizeLeftEmpSearchResults(res);
        const match = normalizedPayrollId
          ? this.hrHttpService.findLeftEmpSearchMatch(apiList, normalizedPayrollId)
          : this.hrHttpService.findLeftEmpSearchMatchByAadhar(apiList, savedAadhar);
        const merged = match ? { ...savedOption, ...match } : savedOption;
        const resolvedId =
          this.hrHttpService.getLeftEmpLookupId(merged) || normalizedPayrollId || lookupKey;
        this.upsertLeftEmpOption(resolvedId, merged);
        this.applyPrevRehiringEmpDetails(merged, { preserveAadhar: savedAadhar });
      },
      error: (err) => {
        console.error('LeftEmpSearch failed during rehiring bind', err);
        this.upsertLeftEmpOption(lookupKey, savedOption);
        this.applyPrevRehiringEmpDetails(savedOption, { preserveAadhar: savedAadhar });
      },
      complete: () => onComplete?.(),
    });
  }

  private buildLeftEmpOptionFromData(
    lookupId: string,
    data: Record<string, unknown>
  ): Record<string, unknown> {
    return {
      payrollId: lookupId,
      payRollId: lookupId,
      employeeId: lookupId,
      empName: this.pickLeftEmpField(
        data,
        'prevEmpName',
        'newEmpName',
        'newEmployeeName',
        'employeeName',
        'candidateName'
      ),
      aadharNo: resolveMrfRehiringAadharNo(data),
      dob: this.pickLeftEmpField(data, 'dob', 'dateOfBirth', 'employeeDob', 'birthDate'),
    };
  }

  private loadRehiringLeftEmpIfNeeded(onComplete?: () => void): void {
    if (!this.rehiring) {
          onComplete?.();
      return;
    }

    const data = this.mrfData ?? {};
    const lookupId = this.hrHttpService.resolveMrfRehiringPayrollId(data);
    const aadharNo = resolveMrfRehiringAadharNo(data);
    if (!lookupId && !aadharNo) {
          onComplete?.();
      return;
    }

    this.fetchAndBindRehiringEmployee(lookupId, data, onComplete);
  }

  private loadRehiringAadhaarDocuments(mrfId: string, onComplete?: () => void): void {
    this.hrHttpService.getMrfDocumentsByMrfFormId(mrfId).subscribe({
      next: (docs) => {
        this.applyDocumentsFromApi(
          docs,
          this.hrHttpService.extractMrfDocumentsFromPayload(this.employeeAssessmentRaw)
        );
      },
      error: () => {},
      complete: () => onComplete?.(),
    });
  }

  private clearRehiringAadhaarUploads(): void {
    const pending = this.sharedAadhaarPendingFiles.slice();
    this.rehiringAadhaarPendingFiles = [];
    this.aadhaarPendingFiles = pending;
    this.rehiringAadhaarUploadError = '';
    this.syncSharedAadhaarSavedDocuments();
  }

  private revokeRehiringAadhaarDocumentUrls(): void {
    for (const doc of this.rehireAadhaarDocuments) {
      const url = doc?.blobUrl;
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    }
  }

  private fetchTempEmpOptions(search: string): Observable<Record<string, unknown>[]> {
    return this.hrHttpService.searchTempEmployees(search).pipe(
      map((res) => this.hrHttpService.normalizeTempEmpSearchResults(res)),
      map((list) => this.mergeTempEmpOptions(this.tempEmpOptionsList, list)),
      map((list) => this.publishTempEmpOptions(list)),
      catchError((err) => {
        console.error('tempEmpSearch failed', err);
        const fallback = this.filterTempEmpOptions(search, this.tempEmpOptionsList);
        return of(this.publishTempEmpOptions(fallback));
      })
    );
  }

  private publishTempEmpOptions(list: Record<string, unknown>[]): Record<string, unknown>[] {
    this.tempEmpOptionsList = list;
    this.tempEmpOptionsSubject.next(list);
    return list;
  }

  private mergeTempEmpOptions(
    existing: Record<string, unknown>[],
    incoming: Record<string, unknown>[]
  ): Record<string, unknown>[] {
    const merged = [...incoming];
    for (const item of existing) {
      const id = this.getTempEmpLookupId(item);
      if (!id) continue;
      const duplicate = merged.some(
        (row) => this.getTempEmpLookupId(row).toLowerCase() === id.toLowerCase()
      );
      if (!duplicate) merged.push(item);
    }
    return merged;
  }

  private filterTempEmpOptions(
    search: string,
    list: Record<string, unknown>[]
  ): Record<string, unknown>[] {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return list.filter((item) => {
      const haystack = [
        this.getTempEmpLookupId(item),
        this.pickTempEmpField(item, 'empName', 'employeeName', 'name', 'userName'),
        this.pickTempEmpField(item, 'mobileNo', 'mobileNumber', 'mobile', 'phoneNo'),
        this.pickTempEmpField(item, 'aadharNo', 'aadhaarNo', 'aadharCardNo'),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  private seedQualifiedEmployeeTempIdFromAssessment(onComplete?: () => void): void {
    if (this.isExamTaken('yes')) {
      this.examTakenYesSnapshot = this.captureQualifiedEmployeeSnapshot();
    }

    const savedId = this.qualifiedFieldValue(
      'employeeTempId',
      'empTempId',
      'tempEmpId',
      'tempPayrollId'
    );
    if (!savedId || !this.isExamTaken('yes')) {
      onComplete?.();
      return;
    }

    const placeholder: Record<string, unknown> = {
      employeeTempId: savedId,
      tempEmpId: savedId,
      empTempId: savedId,
      tempPayrollId: savedId,
      payrollId: savedId,
    };
    this.publishTempEmpOptions([placeholder]);
    this.employeeTempId.setValue(placeholder, { emitEvent: false });
        onComplete?.();
  }

  private applyTempEmpDetails(item: Record<string, unknown>): void {
    const mapped = this.hrHttpService.mapTempEmpToViewData(item);
    this.publishTempEmpOptions([mapped]);
    this.employeeTempId.setValue(mapped, { emitEvent: false });
    this.bindQualifiedEmployeeDetails(mapped);
  }

  private getTempEmpSearchText(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'object' && !Array.isArray(value)) {
      return this.getTempEmpLookupId(value as Record<string, unknown>);
    }
    const text = String(value).trim();
    if (text.includes('|')) {
      return text.split('|')[0]?.trim() || text;
    }
    return text;
  }

  private getTempEmpLookupId(item: Record<string, unknown>): string {
    return this.hrHttpService.getTempEmpLookupId(item);
  }

  private resolveEmployeeTempId(): string {
    const value = this.employeeTempId.value;
    if (value && typeof value === 'object') {
      return this.hrHttpService.getTempEmpLookupId(value as Record<string, unknown>);
    }
    return String(value ?? '').trim();
  }

  private readRawTempPayrollId(result: {
    tempPayrollId?: string;
    employeeData?: Record<string, unknown>;
  }): string {
    const fromResult = this.readRawTempPayrollIdFromRecord({
      tempPayrollId: result.tempPayrollId ?? '',
    });
    if (fromResult) {
      return fromResult;
    }
    return this.readRawTempPayrollIdFromRecord(result.employeeData ?? {});
  }

  private readRawTempPayrollIdFromRecord(data: Record<string, unknown>): string {
    const value = String(data['tempPayrollId'] ?? '').trim();
    return value && value !== '0' ? value : '';
  }

  private hasAttemptedExamTempPayrollId(): boolean {
    if (this.lastAadhaarTempPayrollId) {
      return true;
    }
    return !!this.readRawTempPayrollIdFromRecord(this.employeeAssessmentData ?? {});
  }

  getTempEmpOptionLabel(item: Record<string, unknown>): string {
    const tempId = this.getTempEmpLookupId(item);
    const name = this.pickTempEmpField(item, 'empName', 'employeeName', 'name', 'userName');
    const mobile = this.pickTempEmpField(item, 'mobileNo', 'mobileNumber', 'mobile', 'phoneNo');
    const parts = [tempId, name, mobile].filter((part) => !!part);
    return parts.join(' | ') || tempId || name || 'Employee';
  }

  private pickTempEmpField(item: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
      const val = item[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  }

  private setPrincipalEditableFieldsEnabled(enabled: boolean): void {
    const controls = [
      this.mobileNo,
      this.panNo,
      this.dob,
      this.gender,
      this.employeeTempId,
      this.expectedSalary,
      this.aadhaarCardNo,
    ];
    for (const control of controls) {
      if (enabled) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }

    const identityEditable = enabled && this.canEnterNewEmployeeIdentityFields;
    if (identityEditable) {
      this.employeeName.enable({ emitEvent: false });
      this.surname.enable({ emitEvent: false });
    } else {
      this.employeeName.disable({ emitEvent: false });
      this.surname.disable({ emitEvent: false });
    }
  }

  private getWorkflowStatus(): string {
    return resolveMrfRequestStatusRaw(this.mrfData ?? {});
  }

  get requestStatusLabel(): string {
    return formatMrfRequestStatusLabel(
      resolveMrfRequestStatusRaw(this.mrfData ?? {})
    );
  }

  get requestStatusTone(): string {
    return getMrfRequestStatusTone(resolveMrfRequestStatusRaw(this.mrfData ?? {}));
  }

  get onboardingStatusLabel(): string {
    return formatMrfOnboardingStatusLabel(
      resolveMrfOnboardingStatusRaw(this.mrfData ?? {})
    );
  }

  get onboardingStatusTone(): string {
    return getMrfOnboardingStatusTone(
      resolveMrfOnboardingStatusRaw(this.mrfData ?? {})
    );
  }

  /** Loads replacement / rehiring employee details on form load. */
  private loadReplacementEmployeeDetails(onComplete?: () => void): void {
    const replacementEmpId = this.resolveReplacementEmployeeId();

    let chain$: Observable<Record<string, unknown>> = of(this.mrfData);

    if (this.isReplacement && replacementEmpId) {
      chain$ = chain$.pipe(
        switchMap((data) =>
          this.hrHttpService.loadReplacementViaCurrentAndLeftSearch(data, replacementEmpId, {
            scope: 'replacement',
          })
        )
      );
    }

    if (this.isRehiring) {
      chain$ = chain$.pipe(
        switchMap((data) => this.hrHttpService.loadRehiringViaLeftEmpSearch(data))
      );
    }

    chain$.pipe(finalize(() => this.finishRehiringLoad(onComplete))).subscribe({
      next: (merged) => {
        this.mrfData = this.restoreGetMrfReplacementEmpId(merged);
        this.applyPrincipalFormFields();
      },
      error: (err) => console.error(err),
    });
  }

  private parseReplacementEmpIdFromGetMrf(data: Record<string, unknown> | null): number {
    if (!data) {
      return 0;
    }
    const raw = data['replacementEmpId'];
    const unique = data['uniqueId'] ?? data['empId'] ?? data['id'];
    const pick = (...values: unknown[]): number => {
      for (const value of values) {
        if (value == null || String(value).trim() === '') {
          continue;
        }
        const text = String(value).trim();
        if (text === '0' || !/^\d+$/.test(text)) {
          continue;
        }
        const num = Number(text);
        if (Number.isFinite(num) && num !== 0) {
          return num;
        }
      }
      return 0;
    };
    return pick(raw, unique);
  }

  private resolveReplacementEmpIdForSave(data: Record<string, unknown>): number {
    if (this.originalReplacementEmpId) {
      return this.originalReplacementEmpId;
    }
    return this.parseReplacementEmpIdFromGetMrf(data);
  }

  private restoreGetMrfReplacementEmpId(data: Record<string, unknown>): Record<string, unknown> {
    if (!this.originalReplacementEmpId) {
      return data;
    }
    return {
      ...data,
      replacementEmpId: this.originalReplacementEmpId,
    };
  }

  private finishRehiringLoad(onComplete?: () => void): void {
    const loadDocs = () => {
    const mrfId = this.getMrfFormId();
      if (this.isRehiring && mrfId) {
        this.loadRehiringAadhaarDocuments(mrfId, onComplete);
      } else {
        onComplete?.();
      }
    };

    if (this.isRehiring && this.canEditRehiringSection) {
      const mrfData = this.mrfData ?? {};
      const payrollId = this.hrHttpService.resolveMrfRehiringPayrollId(mrfData);
      const aadharNo = resolveMrfRehiringAadharNo(mrfData);
      if ((payrollId || aadharNo) && !this.prevRehiringEmployeeDetails) {
        if (payrollId) {
          this.lockRehiringFields();
        }
        this.fetchAndBindRehiringEmployee(payrollId, mrfData, loadDocs);
        return;
      }
    }

    loadDocs();
  }

  private resolveReplacementEmployeeId(): string {
    return this.hrHttpService.resolveMrfReplacementEmpId(this.mrfData ?? {});
  }

  private loadQualifiedEmployeeDocuments(
    onComplete?: () => void,
    apiDocs: MrfDocumentView[] = []
  ): void {
    const embeddedFromRaw = this.hrHttpService.extractMrfDocumentsFromPayload(
      this.employeeAssessmentRaw
    );
    const embeddedDocs = embeddedFromRaw.length
      ? embeddedFromRaw
      : this.hrHttpService.extractMrfDocumentsFromPayload({
          ...this.employeeAssessmentData,
          mrfFormId: this.getMrfFormId(),
        });
    this.applyDocumentsFromApi(apiDocs, embeddedDocs);
    onComplete?.();
  }

  private applyDocumentsFromApi(
    apiDocs: MrfDocumentView[],
    embeddedDocs: MrfDocumentView[] = []
  ): void {
    this.revokeDocumentBlobUrls();
    this.documents = this.hrHttpService.mergeMrfDocumentLists(apiDocs, embeddedDocs);
        this.partitionDocuments();
  }

  private loadDocuments(onComplete?: () => void): void {
    const mrfId = this.getMrfFormId();
    if (!mrfId) {
      onComplete?.();
      return;
    }

    this.hrHttpService.getMrfDocumentsByMrfFormId(mrfId).subscribe({
      next: (docs) => {
        const embeddedFromRaw = this.hrHttpService.extractMrfDocumentsFromPayload(
          this.employeeAssessmentRaw
        );
        const embeddedDocs = embeddedFromRaw.length
          ? embeddedFromRaw
          : this.hrHttpService.extractMrfDocumentsFromPayload({
              ...this.employeeAssessmentData,
              mrfFormId: mrfId,
            });
        this.applyDocumentsFromApi(docs, embeddedDocs);
      },
      error: () => {},
      complete: () => onComplete?.(),
    });
  }

  private loadHeadCountFromData(onComplete?: () => void): void {
    if (this.mrfData?.campusId == null) {
      onComplete?.();
      return;
    }

    const headcount = Number(this.mrfData?.noOfHeadcount ?? this.mrfData?.headcount ?? 1);

    this.hrHttpService
      .getMrfHeadCountInfoForMrfData(this.mrfData ?? {})
      .pipe(finalize(() => onComplete?.()))
      .subscribe({
      next: (res: unknown) => {
        const parsed = this.hrHttpService.parseMrfHeadCountResponse(res, headcount);
        this.budgetedPositions = String(parsed.budgetedPositions || '');
        this.availableHeadcount = String(parsed.availableHeadcount);
      },
      error: (err) => console.error(err),
    });
  }

  private loadAvailableBudgetFromData(onComplete?: () => void): void {
    this.hrHttpService
      .getMrfAvailableBudgetForMrfData(this.mrfData ?? {})
      .pipe(finalize(() => onComplete?.()))
      .subscribe({
      next: (budget) => {
        this.applyAvailableBudgetView(budget);
      },
      error: (err) => console.error(err),
    });
  }

  private applyAvailableBudgetView(budget: MrfAvailableBudgetView | null): void {
    if (!budget) {
      this.budgetAmount = null;
      this.totalUsedBudget = null;
      this.totalExpectedSalBudget = null;
      this.availableBudgetAmount = null;
      return;
    }
    this.budgetAmount = budget.budgetAmount;
    this.totalUsedBudget = budget.totalUsedBudget;
    this.totalExpectedSalBudget = budget.totalExpectedSal;
    this.availableBudgetAmount = budget.availableBudget;
  }

  private getMrfFormId(): string {
    return this.route.snapshot.queryParamMap.get('mrfId') ?? '';
  }

  private buildEmployeeAssessmentPayload(mrfId: string) {
    const expectedSalNum = parseMrfAmountNumber(this.expectedSalary.value) ?? undefined;
    const tempPayrollId =
      this.resolveEmployeeTempId() || resolveMrfTempPayrollId(this.employeeAssessmentData);
    const surname = this.canEnterNewEmployeeIdentityFields
      ? String(this.surname.value ?? '').trim()
      : resolveMrfSurname(this.employeeAssessmentData);
    return {
      mrfFormId: Number(mrfId),
      employeeName: this.canEnterNewEmployeeIdentityFields
        ? String(this.employeeName.value ?? '').trim()
        : this.qualifiedFieldValue('newEmpName', 'newEmployeeName', 'candidateName', 'employeeName'),
      surname: surname || undefined,
      mobileNo: normalizeMrfMobileDigits(this.mobileNo.value),
      aadhaarNo:
        normalizeMrfAadhaarDigits(this.aadhaarCardNo.value) ||
        normalizeMrfAadhaarDigits(
          this.qualifiedFieldValue(
        'newEmpAadhar',
        'aadharCardNo',
        'candidateAadhar',
        'aadharNo',
        'aadhaarNo'
          )
      ),
      panNo: normalizeMrfPanNo(this.panNo.value),
      gender: String(normalizeMrfGenderValue(this.gender.value ?? '')).trim(),
      dob: formatMrfDateForPayload(this.dob.value),
      expectedSal: expectedSalNum,
      examTaken: this.examTaken === 'yes',
      tempPayrollId: tempPayrollId || '',
    };
  }

  private initExamTakenFromData(): void {
    if (this.employeeAssessmentErrored) {
      this.examTaken = 'no';
      return;
    }

    if (!this.isTestSubject) {
      this.examTaken = 'no';
      return;
    }

    this.lastAadhaarTempPayrollId = this.readRawTempPayrollIdFromRecord(
      this.employeeAssessmentData ?? {}
    );

    const v =
      this.mrfData?.examTaken ?? this.mrfData?.isExamTaken ?? this.mrfData?.examTakenFlag;
    if (v === true || v === 'true' || v === 'yes' || v === 'Y' || v === 1) {
      this.examTaken = 'yes';
    } else {
      this.examTaken = 'no';
    }
  }

  private getMaxFiles(type: MrfPdDocSlot): number {
    if (type === 'aadhaar') return this.maxAadhaarFiles;
    if (type === 'pan') return this.maxPanFiles;
    if (type === 'exam') return this.maxExamFiles;
    return this.maxDemoFiles;
  }

  private getPendingFiles(type: MrfPdDocSlot): File[] {
    if (type === 'aadhaar') return this.aadhaarPendingFiles;
    if (type === 'pan') return this.panPendingFiles;
    if (type === 'exam') return this.examPendingFiles;
    return this.demoVideoPendingFiles;
  }

  private setPendingFiles(type: MrfPdDocSlot, files: File[]): void {
    if (type === 'aadhaar') {
      this.syncSharedAadhaarPendingFiles(files);
      return;
    }
    if (type === 'pan') this.panPendingFiles = files;
    else if (type === 'exam') this.examPendingFiles = files;
    else this.demoVideoPendingFiles = files;
  }

  private getAllPendingFiles(): File[] {
    return [
      ...this.aadhaarPendingFiles,
      ...this.panPendingFiles,
      ...this.examPendingFiles,
      ...this.demoVideoPendingFiles,
    ];
  }

  private clearPendingFiles(): void {
    this.syncSharedAadhaarPendingFiles([]);
    this.panPendingFiles = [];
    this.examPendingFiles = [];
    this.demoVideoPendingFiles = [];
    this.rehiringAadhaarUploadError = '';
  }

  private partitionDocuments(): void {
    const buckets = this.hrHttpService.partitionMrfDocuments(this.documents, {
      examTaken: this.isExamTaken('yes'),
    });
    this.aadhaarDocuments = buckets.aadhaar;
    this.panDocuments = buckets.pan;
    this.examDocuments = buckets.exam;
    this.demoVideoDocuments = buckets.demoVideo;
    this.syncSharedAadhaarSavedDocuments();
  }

  private syncSharedAadhaarSavedDocuments(): void {
    const merged = this.hrHttpService.mergeMrfDocumentLists(
      this.aadhaarDocuments,
      this.rehireAadhaarDocuments
    );
    this.aadhaarDocuments = merged;
    this.rehireAadhaarDocuments = merged;
  }

  private syncSharedAadhaarPendingFiles(files: File[]): void {
    const slice = files.slice(0, this.maxAadhaarFiles);
    this.aadhaarPendingFiles = slice;
    this.rehiringAadhaarPendingFiles = slice;
  }
}
